import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { cqAPI, getBid } from "../../lib/Auth";
import Messages from "./Messages";
import "./Inbox.css";
import { Flex, useColorMode, useToast } from "@chakra-ui/react";
import { useAppContext } from "../../AppContext";
import SendArea from "./TeamArea";
import GroupChatInfo from "./GroupChatInfo";
const _ = require("lodash");

function GroupChatMessages({ removeActionBarItem, addActionBarItem, isMobile }) {
  const { groupId } = useParams();
  const bid = getBid();
  const [messages, setMessages] = useState([]);
  const [group, setGroup] = useState(null);
  const [disableSendButton, setDisableSendButton] = useState(false);
  const [conversationClosed, setConversationClosed] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const toast = useToast();
  const { colorMode } = useColorMode();
  const {
    staff,
    setStaff,
    realtime,
    realtimeInitialized,
    location,
    isPatient,
  } = useAppContext();

  const [userInfo, setUserInfo] = useState({});

  // Pagination state
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchAllMessages = async (update = false, p = 0) => {
    const response = await cqAPI.get(`/group-chat/${groupId}/messages?page=${p}`);
    if (response.type === "error") {
      toast({
        title: "Error",
        description: response.msg,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    const fetched = response.data.conversations || [];
    setGroup(response.data.group);

    const combined =
      p === 0
        ? _.uniqBy([...messages, ...fetched], "_id")
        : _.uniqBy([...fetched, ...messages], "_id");

    setMessages(combined);

    if (fetched.length === 0 && p > 0) {
      setHasMore(false);
    }

    // Build a userInfo-like object for the group (used by Messages component)
    // Messages.js convention: userInfo.user_id matches author → LEFT, doesn't match → RIGHT
    // So set user_id to a dummy "others" value; we'll remap message authors to match
    if (response.data.group) {
      setUserInfo({
        user_name: response.data.group.name,
        user_id: '_others_',
      });
      setCurrentUserId(response.data.group.current_user_id);
    }

    // Determine closed state for current user
    if (response.data.group) {
      const isClosed = (response.data.group.closed_by || []).some(
        uid => String(uid) === String(response.data.group.current_user_id)
      );
      setConversationClosed(isClosed);
    }

    if (!staff.length) {
      const s = await cqAPI.get("/list-staff");
      if (s.data && s.data.length) {
        setStaff(s.data);
      }
    }

    return true;
  };

  const handleNewMessage = async (update) => {
    setPage(0);
    setHasMore(true);
    await fetchAllMessages(update, 0);
  };

  useEffect(() => {
    if (!groupId) return;
    setPage(0);
    setHasMore(true);
    setMessages([]);
    fetchAllMessages(false, 0);
  }, [groupId]);

  // Subscribe to realtime updates
  useEffect(() => {
    if (!realtime.current) return;
    // Subscribe to the current user's topic so we catch messages that are fanned out
    const topic = `_new_chat_message/${bid}`;
    realtime.current.subscribeToTopic(topic, bid, handleNewMessage);
  }, [realtimeInitialized, groupId, realtime]);

  useEffect(() => {
    const current = realtime?.current;
    return () => {
      if (!current) return;
      const topic = `_new_chat_message/${bid}`;
      current.unsubscribeFromTopic(topic, bid, () => handleNewMessage(true));
    };
  }, [location, realtime]);

  const loadMoreMessages = async (oldScrollHeight, oldScrollTop, scrollContainer) => {
    if (!hasMore) return;
    setIsLoadingMore(true);
    const nextPage = page + 1;
    const success = await fetchAllMessages(true, nextPage);
    if (success) {
      setPage(nextPage);
      requestAnimationFrame(() => {
        const newScrollHeight = scrollContainer.scrollHeight;
        scrollContainer.scrollTop = newScrollHeight - oldScrollHeight + oldScrollTop;
      });
    }
    setIsLoadingMore(false);
  };

  // Custom submit handler for SendArea — posts to group endpoint instead of /messages
  const handleGroupSubmit = async (body, attachments, mentions) => {
    const response = await cqAPI.post(`/group-chat/${groupId}/messages`, {
      body,
      attachments,
      mentions: mentions || [],
    });
    // After successful send, refresh messages
    if (response.type === "ok") {
      await fetchAllMessages(true, 0);
    }
    return response;
  };

  // Presentation-only transform: remap author for Messages.js left/right convention
  // This keeps the raw `messages` state untouched
  const displayMessages = useMemo(() =>
    messages.map(m => ({
      ...m,
      author: m.author === currentUserId ? m.author : '_others_'
    })),
    [messages, currentUserId]
  );

  return (
    <Flex className="inbox-container" direction="column">
      <GroupChatInfo
        group={group}
        groupId={groupId}
        addActionBarItem={addActionBarItem}
        removeActionBarItem={removeActionBarItem}
        isMobile={isMobile}
        onGroupUpdated={handleNewMessage}
        conversationClosed={conversationClosed}
        setConversationClosed={setConversationClosed}
      />
      <div className="chat-area intercom">
        <Messages
          messages={displayMessages}
          disableSendButton={disableSendButton}
          userInfo={userInfo}
          colorMode={colorMode}
          isPatient={isPatient}
          fetchAllMessages={fetchAllMessages}
          setMessages={setMessages}
          isMobile={isMobile}
          hasMore={hasMore}
          onLoadMore={loadMoreMessages}
          isLoadingMore={isLoadingMore}
          page={page}
        />
        <SendArea
          toast={toast}
          colorMode={colorMode}
          userInfo={userInfo}
          disableSendButton={disableSendButton}
          setDisableSendButton={setDisableSendButton}
          uid={groupId}
          isMobile={isMobile}
          staff={staff}
          onSubmit={handleGroupSubmit}
        />
      </div>
    </Flex>
  );
}

export default GroupChatMessages;
